<script>
import settingsMixin from '@/views/Admin/Config/Settings/Common/settingsMixin';
import SettingsPage from '@/views/Admin/Config/Settings/Common/SettingsPage';
import HelpModal from '@/components/HelpModal.vue';
import SettingForm from '@/views/Admin/Config/Settings/Common/SettingForm.vue';
import BrandingImageUpload from '@/views/Admin/Config/Settings/Common/BrandingImageUpload.vue';
import TabPreview from '@/views/Admin/Config/Settings/Common/TabPreview.vue';

import { useWhiteLabel } from '@/store/useWhitelabel';

const wl = useWhiteLabel();

export default {
    components: { TabPreview, BrandingImageUpload, HelpModal, SettingForm: SettingForm, SettingsPage },
    mixins: [settingsMixin],
    props: [],

    computed: {
        wl() {
            return wl;
        }
    },

    data() {
        return {
            logo: null,
            favicon: null,
            logoUploading: false,
            faviconUploading: false,
            logoError: [],
            faviconError: []
        };
    }
};
</script>

<template>
    <SettingsPage title="White Label">
        <template #settingsForms>
            <v-alert
                style="width: 100%"
                type="info"
                color="primary"
            >
                Refresh the page after clicking "Save" to see your changes.
            </v-alert>

            <SettingForm
                page="application"
                setting-name="settingApplication"
            >
                <template #title>
                    Application
                </template>

                <template #helpModal>
                    <HelpModal
                        title="Application Settings"
                        text="
                        <dl>
                            <dt>Application Name</dt>
                            <dd>This name is used throughout the app, such as in the browser tab and in email communications.</dd>
                            <dt>Copyright Name</dt>
                            <dd>The name to display in the copyright statement at the bottom of the page.</dd>
                            <dt>Trademarked/Registered</dt>
                            <dd>Display &trade; or &reg; symbol after the name.</dd>
                        </dl>
                        "
                    />
                </template>

                <template #settingForm="{ submitFn, settingData }">
                    <div class="WhitelabelForm">
                        <div class="WhitelabelForm__preview">
                            <TabPreview
                                :title="wl.setAppNamePreview(
                                    settingData.applicationName,
                                    settingData.registered,
                                    settingData.trademarked
                                )"
                            />
                        </div>

                        <div class="WhitelabelForm__form">
                            <form
                                id="applicationForm"
                                name="applicationForm"
                                method="POST"
                                action=""
                                class="SettingsWhiteLabel__form"
                                @submit.prevent="submitFn('application', 'settingApplication', settingData)"
                            >
                                <ValidationProvider
                                    v-slot="{ errors }"
                                    tag="div"
                                    class="SettingsWhiteLabel__form__input"
                                    name="applicationName"
                                    rules="max:255"
                                >
                                    <v-text-field
                                        id="applicationName"
                                        v-model="settingData.applicationName"
                                        label="Application Name"
                                        name="applicationName"
                                        outlined
                                        :error-messages="errors[0]"
                                    />
                                </ValidationProvider>

                                <ValidationProvider
                                    v-slot="{ errors }"
                                    tag="div"
                                    class="SettingsWhiteLabel__form__input"
                                    name="copyrightName"
                                    rules="max:255"
                                >
                                    <v-text-field
                                        id="copyrightName"
                                        v-model="settingData.copyrightName"
                                        label="Copyright Name"
                                        name="copyrightName"
                                        outlined
                                        :error-messages="errors[0]"
                                    />
                                </ValidationProvider>

                                <div class="SettingsWhiteLabel__form__checkboxes">
                                    <v-checkbox
                                        v-model="settingData.trademarked"
                                        :label="'Trademarked'"
                                        class="SettingsWhiteLabel__form__checkbox"
                                    />
                                    <v-checkbox
                                        v-model="settingData.registered"
                                        :label="'Registered'"
                                        class="SettingsWhiteLabel__form__checkbox"
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </template>
            </SettingForm>

            <SettingForm
                page="whitelabel"
                setting-name="settingWhitelabel"
            >
                <template #title>
                    Theme
                </template>

                <template #helpModal>
                    <HelpModal
                        title="Theme Settings"
                        text="
                        <dl>
                            <dt>Primary Color</dt>
                            <dd>
                                The primary color should contrast properly against the following colors: <br />
                                <ul>
                                <li>Light Gray: #f3f6f9</li>
                                <li>Dark Gray: #292d34</li>
                                </ul>
                                The primary color is used for:
                                <ul>
                                <li>Primary action buttons</li>
                                <li>Links</li>
                                <li>Checkboxes</li>
                                <li>Help Icons</li>
                                <li>Sidebar active item indicators</li>
                                <li>Sidebar item hover indicator</li>
                                <li>Half of the gradient</li>
                                </ul>
                            </dd>

                            <dt>Secondary Color</dt>
                            <dd>
                                The secondary color should contrast properly against the following colors: <br />
                                <ul>
                                <li>Light gray: #f3f6f9</li>
                                </ul>
                                The secondary color is used for:
                                <ul>
                                <li>Secondary action buttons</li>
                                <li>Half of the gradient</li>
                                </ul>
                            </dd>

                            <dt>Gradient</dt>
                            <dd>
                                The gradient is used for:
                                <ul>
                                <li>Background of the page header</li>
                                <li>Background of authentication pages</li>
                                </ul>
                            </dd>
                        </dl>
                        "
                    />
                </template>

                <template #settingForm="{ submitFn, settingData }">
                    <div class="WhitelabelForm">
                        <div class="WhitelabelForm__preview">
                            <div
                                class="SettingsWhiteLabel__swatches"
                                role="presentation"
                            >
                                <div
                                    class="SettingsWhiteLabel__swatches__swatch"
                                    :style="'background-color:' + settingData.primaryColor"
                                >
                                    Primary
                                </div>

                                <div
                                    class="SettingsWhiteLabel__swatches__swatch"
                                    :style="'background-color:' + settingData.secondaryColor"
                                >
                                    Secondary
                                </div>

                                <div
                                    v-if="settingData.useGradient"
                                    class="SettingsWhiteLabel__swatches__swatch"
                                    :style="
                                        'background: linear-gradient(90deg, '
                                            + (settingData.reverseGradient
                                                ? settingData.primaryColor
                                                : settingData.secondaryColor
                                            )
                                            + ' 0%, '
                                            + (settingData.reverseGradient
                                                ? settingData.secondaryColor
                                                : settingData.primaryColor
                                            )
                                            + ' 100%) !important;'
                                    "
                                >
                                    Gradient <span v-if="settingData.reverseGradient">&nbsp;(Reversed)</span>
                                </div>
                            </div>
                        </div>
                        <div class="WhitelabelForm__form">
                            <form
                                id="whitelabelForm"
                                name="whitelabelForm"
                                method="POST"
                                action=""
                                class="SettingsWhiteLabel__form"
                                @submit.prevent="submitFn('whitelabel', 'settingWhitelabel', settingData)"
                            >
                                <ValidationProvider
                                    v-slot="{ errors }"
                                    tag="div"
                                    class="SettingsWhiteLabel__form__input"
                                    name="primaryColor"
                                    rules="max:7"
                                >
                                    <v-text-field
                                        id="primaryColor"
                                        v-model="settingData.primaryColor"
                                        label="Primary Color"
                                        name="primaryColor"
                                        outlined
                                        :error-messages="errors[0]"
                                    />
                                </ValidationProvider>

                                <ValidationProvider
                                    v-slot="{ errors }"
                                    tag="div"
                                    class="SettingsWhiteLabel__form__input"
                                    name="secondaryColor"
                                    rules="max:7"
                                >
                                    <v-text-field
                                        id="secondaryColor"
                                        v-model="settingData.secondaryColor"
                                        label="Secondary Color"
                                        name="secondaryColor"
                                        outlined
                                        :error-messages="errors[0]"
                                    />
                                </ValidationProvider>

                                <div class="SettingsWhiteLabel__form__checkboxes">
                                    <v-checkbox
                                        v-model="settingData.useGradient"
                                        :label="'Use Gradient'"
                                        class="SettingsWhiteLabel__form__checkbox"
                                    />
                                    <v-checkbox
                                        v-if="settingData.useGradient"
                                        v-model="settingData.reverseGradient"
                                        :label="'Reverse Gradient'"
                                        class="SettingsWhiteLabel__form__checkbox"
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </template>
            </SettingForm>

            <BrandingImageUpload
                accept=".jpg, .png"
                form-id="whitelabelLogoUpload"
                form-name="whitelabelLogoUpload"
                card-title="Logo"
                hint=".png or .jpg file only, must be less than 10mb"
                item-slug="logo"
            />

            <BrandingImageUpload
                accept=".png"
                form-id="whitelabelFaviconUpload"
                form-name="whitelabelFaviconUpload"
                card-title="Favicon"
                hint=".png file only, must be less than 2mb"
                item-slug="favicon"
            />
        </template>
    </SettingsPage>
</template>

<style lang="scss">
.SettingsWhiteLabel__swatches {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 24px;
}

.SettingsWhiteLabel__swatches__swatch {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    height: 48px;
    margin-bottom: 1px;
    color: white !important;
    padding: 0 12px;
    font-weight: bold;
    font-size: 1.05rem;
}

.SettingsWhiteLabel__form {
    display: flex;
    flex-flow: row wrap;
}

.SettingsWhiteLabel__form__input {
    flex: 0 0 100%;
    width: 100%;
}

.SettingsWhiteLabel__form__checkboxes {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    margin-top: -20px !important;


    @media (min-width: 768px) {
        flex-flow: row nowrap;
        flex: 0 0 50%;
        width: 50%;

        .v-input + .v-input {
            margin-left: 24px;
        }
    }
}

.SettingsWhiteLabel__form__checkbox {
    flex: 0 0 100%;
    width: 100%;

    .v-input__slot {
        margin-bottom: 0 !important;
    }

    .v-messages {
        display: none !important;
    }
}

.WhitelabelForm {
    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: 768px) {
        grid-template-columns: 1fr 3fr;
        gap: 48px;
    }
}
</style>

